// Thin typed wrappers for /api/v1. The dev server proxies /api → :8098.
//
// All HTTP transport delegates to @avagency/frontend/lib/api.apiFetch.
// This file owns typed resource interfaces, URL templates, and the
// AUTH_INVALIDATE_EVENT layer that bridges 401s into a useMe refetch.

import { apiFetch, ApiError } from '@avagency/frontend/lib/api';

// ── Auth ─────────────────────────────────────────────────────────────────────

export interface Me {
  tenant_id: string;
  user_id: string;
  email: string;
}

export const AUTH_INVALIDATE_EVENT = 'pipeline-rot:auth:invalidate';

// rotFetch wraps apiFetch with AUTH_INVALIDATE_EVENT side-effect on 401.
async function rotFetch<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  try {
    return await apiFetch<T>(path, init);
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 401 && !path.includes('/api/v1/auth/me')) {
        window.dispatchEvent(new CustomEvent(AUTH_INVALIDATE_EVENT));
      }
      const decorated = err as Error & { status?: number };
      decorated.status = err.status;
      throw decorated;
    }
    throw err;
  }
}

export const fetchMe = (): Promise<Me> =>
  rotFetch<Me>('/api/v1/auth/me');

export const signOut = (): Promise<void> =>
  rotFetch<void>('/api/v1/auth/signout', { method: 'POST' });

export const signInURL = (returnTo = '/'): string =>
  `/api/v1/auth/signin?return_to=${encodeURIComponent(returnTo)}`;

// ── Integrations ─────────────────────────────────────────────────────────────

export interface IntegrationRef {
  id: string;
  provider: string;
  external_id: string;
  display_name: string;
  status: 'active' | 'reauth_required' | 'revoked';
  scopes: string[];
  created_at: string;
}

export interface IntegrationListResponse {
  integrations: IntegrationRef[];
}

export const fetchIntegrations = (): Promise<IntegrationListResponse> =>
  rotFetch<IntegrationListResponse>('/api/v1/integrations');

export const disconnectIntegration = (id: string): Promise<void> =>
  rotFetch<void>(`/api/v1/integrations/${encodeURIComponent(id)}/disconnect`, { method: 'POST' });

// ── Billing ───────────────────────────────────────────────────────────────────

export interface BillingStatus {
  plan: 'free' | 'alerts';
  alerts_enabled: boolean;
  show_hubspot_upsell_banner: boolean;
}

export const fetchBillingStatus = (): Promise<BillingStatus> =>
  rotFetch<BillingStatus>('/api/v1/billing/status');

// POST /api/v1/billing/upgrade → Stripe checkout URL or 503 (waitlist mode).
// Requires: { email, source } — email is validated server-side.
export interface BillingUpgradeResponse {
  checkout_url: string;
}

export const startBillingUpgrade = (email: string): Promise<BillingUpgradeResponse> =>
  rotFetch<BillingUpgradeResponse>('/api/v1/billing/upgrade', {
    method: 'POST',
    json: { email, source: 'billing-page' },
  });

// POST /api/v1/public/waitlist — unauthenticated landing capture.
export const joinWaitlist = (email: string, source: string): Promise<void> =>
  rotFetch<void>('/api/v1/public/waitlist', {
    method: 'POST',
    json: { email, source },
  });

// ── Leaderboard (P10) ─────────────────────────────────────────────────────────

export interface LeaderboardItem {
  id: string;
  integration_id: string;
  external_id: string;
  amount_minor: number;
  currency: string;
  stage: string;
  stage_age_days: number;
  last_activity_age_days: number;
  severity: string;
  owner_name: string;
  owner_email: string;
  snapshot_id: string;
  scanned_at: string;
}

export interface LeaderboardResponse {
  leaderboard: LeaderboardItem[];
}

export const fetchLeaderboard = (): Promise<LeaderboardResponse> =>
  rotFetch<LeaderboardResponse>('/api/v1/leaderboard');

// ── Deals (P10) ───────────────────────────────────────────────────────────────

export interface DealScored {
  id: string;
  integration_id: string;
  external_id: string;
  amount_minor: number;
  currency: string;
  stage: string;
  is_rotting: boolean;
  stage_age_days: number;
  last_activity_age_days: number;
  severity: string;
  owner_name: string;
  owner_email: string;
  snapshot_id: string;
  scanned_at: string;
}

export interface DealsResponse {
  deals: DealScored[];
  total: number;
  page: number;
  page_size: number;
}

export interface DealsQuery {
  stage?: string;
  is_rotting?: boolean;
  page?: number;
  page_size?: number;
}

export const fetchDeals = (q: DealsQuery = {}): Promise<DealsResponse> => {
  const params = new URLSearchParams();
  if (q.stage) params.set('stage', q.stage);
  if (q.is_rotting !== undefined) params.set('is_rotting', String(q.is_rotting));
  if (q.page) params.set('page', String(q.page));
  if (q.page_size) params.set('page_size', String(q.page_size));
  const qs = params.toString();
  return rotFetch<DealsResponse>(`/api/v1/deals${qs ? `?${qs}` : ''}`);
};

// ── Snapshots (P10) ───────────────────────────────────────────────────────────

export interface SnapshotRef {
  id: string;
  integration_id: string;
  provider: string;
  scanned_at: string;
  deal_count: number;
  rotting_count: number;
}

export interface SnapshotsResponse {
  snapshots: SnapshotRef[];
}

export const fetchSnapshots = (integrationID?: string): Promise<SnapshotsResponse> => {
  const params = integrationID
    ? `?integration_id=${encodeURIComponent(integrationID)}`
    : '';
  return rotFetch<SnapshotsResponse>(`/api/v1/snapshots${params}`);
};

// ── Thresholds (P10) ─────────────────────────────────────────────────────────

export interface Threshold {
  id: string;
  integration_id: string;
  stage_name: string;
  threshold_days: number;
  updated_at: string;
}

export interface ThresholdsResponse {
  thresholds: Threshold[];
}

export const fetchThresholds = (): Promise<ThresholdsResponse> =>
  rotFetch<ThresholdsResponse>('/api/v1/thresholds');

export interface UpsertThresholdInput {
  integration_id: string;
  stage_name: string;
  threshold_days: number;
}

export const upsertThreshold = (input: UpsertThresholdInput): Promise<Threshold> =>
  rotFetch<Threshold>('/api/v1/thresholds', {
    method: 'PUT',
    json: input,
  });

// ── OAuth connect (platform server routes) ────────────────────────────────────
//
// OAuth start: POST with { return_url } → server returns { authorize_url }.
// Caller sets window.location.href to authorize_url to begin the OAuth dance.
// The return_url is where the OAuth server redirects after callback success/error.
//
// Freshsales is not OAuth — uses POST /connect-key with { tenant_id, key, extra }.
// The platform server's handleConnectKey requires { tenant_id, key, extra? }.
// domain is passed as extra.base_url (Freshsales API base URL convention).

// return_url MUST be a relative path — oauth_bridge.go:97 rejects absolute URLs with scheme.
const OAUTH_RETURN_URL = '/'; // relative; server appends av_oauth_status query on callback

export const startHubSpotOAuth = async (): Promise<void> => {
  const resp = await rotFetch<{ authorize_url: string }>('/v1/oauth/hubspot/start', {
    method: 'POST',
    json: { return_url: OAUTH_RETURN_URL },
  });
  window.location.href = resp.authorize_url;
};

export const startPipedriveOAuth = async (): Promise<void> => {
  const resp = await rotFetch<{ authorize_url: string }>('/v1/oauth/pipedrive/start', {
    method: 'POST',
    json: { return_url: OAUTH_RETURN_URL },
  });
  window.location.href = resp.authorize_url;
};

export const startZohoOAuth = async (): Promise<void> => {
  const resp = await rotFetch<{ authorize_url: string }>('/v1/oauth/zoho_crm/start', {
    method: 'POST',
    json: { return_url: OAUTH_RETURN_URL },
  });
  window.location.href = resp.authorize_url;
};

// Freshsales connects via API key (not OAuth).
// Platform route: POST /v1/oauth/freshsales/connect-key
// Body: { key: <api_key>, extra: { base_url: <https://domain.freshsales.io> } }
export const connectFreshsalesByKey = (apiKey: string, domain: string): Promise<void> =>
  rotFetch<void>('/v1/oauth/freshsales/connect-key', {
    method: 'POST',
    json: {
      key: apiKey,
      extra: { base_url: `https://${domain}.freshsales.io` },
    },
  });

// parseOAuthCallbackStatus reads av_oauth_status / av_oauth_error from the URL.
export type OAuthCallbackStatus = 'success' | 'error' | null;

export function parseOAuthCallbackStatus(): {
  status: OAuthCallbackStatus;
  error: string | null;
} {
  const sp = new URLSearchParams(window.location.search);
  const s = sp.get('av_oauth_status');
  const e = sp.get('av_oauth_error');
  if (s === 'success') return { status: 'success', error: null };
  if (s === 'error') return { status: 'error', error: e };
  return { status: null, error: null };
}

export function clearOAuthCallbackStatusFromURL(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('av_oauth_status');
  url.searchParams.delete('av_oauth_error');
  window.history.replaceState(null, '', url.toString());
}
